import { useState, useCallback, useEffect, useRef } from 'react';
import toast from 'react-hot-toast';
import type { ConnectionHookResult, ProviderCode } from '@interfaces/Integration';
import { WhatsappConnectionState, WhatsappConnectionResponse } from '../types';
import { useSyncIntegration, useRemoveIntegration, useGetIntegrations } from '../../../../hooks';
import { PROVIDER_TYPE_MAP, Integration } from '@utils/constants';

const INITIAL_STATE: WhatsappConnectionState = {
  isQrVisible: false,
  qrCode: null,
  connectionStatus: 'DISCONNECTED',
  isConnecting: false,
  isVerifying: false,
  instanceName: null,
  error: null,
};

interface UseWhatsappConnectionProps {
  id?: string;
  provider_code: ProviderCode;
  provider_type: keyof typeof PROVIDER_TYPE_MAP;
  configuration: Record<string, unknown>;
}

export const useWhatsappConnection = (integration?: UseWhatsappConnectionProps): ConnectionHookResult => {
  const { syncIntegration } = useSyncIntegration();
  const { removeIntegration } = useRemoveIntegration();
  const { refreshIntegrations } = useGetIntegrations();

  // Guard: solo ejecuta fetchStatus UNA VEZ por ID único.
  // Evita el loop infinito: refreshIntegrations → re-render Card → nuevo objeto integration → useEffect se dispara → fetchStatus → CONNECTED → refreshIntegrations → ...
  const lastCheckedIdRef = useRef<string | null>(null);

  // Guard: evita que un fetchStatus inflight sobreescriba el estado tras desconectar.
  // Race condition: fetchStatus (mount) → responde CONNECTED → override disconnect.
  const isDisconnectingRef = useRef(false);
  const isVerifyCancelledRef = useRef(false);

  const [state, setState] = useState<WhatsappConnectionState>(() => {
    if (integration?.id) {
      return {
        ...INITIAL_STATE,
        connectionStatus: 'CONNECTED',
        instanceName: (integration.configuration?.instance_name as string) || null,
      };
    }
    return { ...INITIAL_STATE };
  });

  const handleConnectionResponse = useCallback(
    (response: WhatsappConnectionResponse) => {
      if (response.status === 'QR_CODE' && response.qrcode) {
        setState((prev) => ({
          ...prev,
          isConnecting: false,
          isVerifying: false,
          isQrVisible: true,
          qrCode: response.qrcode!,
          connectionStatus: 'QR_CODE',
          instanceName: response.instanceName || null,
          error: null,
        }));
      } else if (response.status === 'CONNECTED') {
        setState((prev) => {
          // Si estamos desconectando, ignorar respuestas CONNECTED de fetchStatus en vuelo
          if (isDisconnectingRef.current) {
            return prev;
          }
          // Prevent loop and toast spam if already connected
          if (prev.connectionStatus === 'CONNECTED') {
            return prev;
          }

          // Only toast and refresh if status CHANGED to CONNECTED
          refreshIntegrations();
          toast.success('¡WhatsApp conectado!');

          return {
            ...prev,
            isConnecting: false,
            isVerifying: false,
            isQrVisible: false,
            qrCode: null,
            connectionStatus: 'CONNECTED',
            instanceName: response.instanceName || null,
            error: null,
          };
        });
      } else if (response.status === 'DISCONNECTED') {
        setState((prev) => ({
          ...prev,
          isConnecting: false,
          isVerifying: false,
          connectionStatus: 'DISCONNECTED',
          error: response.message || 'Desconectado',
        }));
      }
    },
    [refreshIntegrations],
  );

  const fetchStatus = useCallback(async () => {
    try {
      const response = (await syncIntegration(integration as unknown as Integration, {
        action: 'SHOW_STATUS',
        notify: false,
        config: {
          instance_name: (integration?.configuration?.instance_name as string) || null,
        },
      })) as unknown as WhatsappConnectionResponse;
      if (response) {
        // SHOW_STATUS es una verificación pasiva: NO debe abrir el modal QR.
        // Si el backend responde QR_CODE, significa que la sesión expiró → marcar DISCONNECTED.
        // Solo connect() (acción del usuario) debe abrir el modal QR.
        if (response.status === 'QR_CODE') {
          setState((prev) => ({
            ...prev,
            connectionStatus: 'DISCONNECTED',
            isConnecting: false,
            instanceName: response.instanceName || prev.instanceName,
          }));
        } else {
          handleConnectionResponse(response);
        }
        return response;
      }
    } catch (error) {
      return null;
    }
  }, [integration, syncIntegration, handleConnectionResponse]); // Limpio: sin state.instanceName

  // Verifica estado real al montar — UNA SOLA VEZ por ID.
  // Detecta desconexiones que ocurrieron mientras el usuario no estaba en el panel
  // (ej: teléfono se apagó, sesión WhatsApp expiró).
  useEffect(() => {
    if (integration?.id && integration.id !== lastCheckedIdRef.current) {
      lastCheckedIdRef.current = integration.id;
      fetchStatus();
    }
  }, [integration?.id, fetchStatus]);

  const connect = useCallback(async () => {
    if (!integration) {
      toast.error('Error de configuración: No se encontró la integración');
      return;
    }

    setState((prev) => ({ ...prev, isConnecting: true, error: null }));

    try {
      const response = (await syncIntegration(integration as unknown as Integration, {
        action: 'CONNECT',
        notify: false,
      })) as unknown as WhatsappConnectionResponse;

      if (response) {
        handleConnectionResponse(response);
      }
    } catch (error) {
      const err = error as { response?: { status: number }; message?: string };

      // Si el error es 403 (Instancia ya existe), intentamos obtener el status
      if (
        err?.response?.status === 403 ||
        err?.message?.includes('already in use') ||
        err?.message?.includes('403') ||
        JSON.stringify(err).includes('already in use')
      ) {
        const statusResponse = await fetchStatus();
        if (statusResponse?.status === 'CONNECTED') {
          return;
        }
      }

      setState((prev) => ({
        ...prev,
        isConnecting: false,
        error: 'Error al conectar. Intenta nuevamente.',
      }));
    }
  }, [integration, syncIntegration, handleConnectionResponse, fetchStatus]);

  const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

  const verifyConnection = useCallback(async () => {
    if (!integration) return;

    isVerifyCancelledRef.current = false;
    setState((prev) => ({ ...prev, isVerifying: true, error: null }));

    let attempts = 0;
    const maxAttempts = 15;
    const pollInterval = 3000;

    try {
      while (attempts < maxAttempts) {
        // Check cancellation before each poll
        if (isVerifyCancelledRef.current) return;

        const response = (await syncIntegration(integration as unknown as Integration, {
          action: 'SHOW_STATUS',
          notify: false,
          config: {
            instance_name: (integration?.configuration?.instance_name as string) || null,
          },
        })) as unknown as WhatsappConnectionResponse;

        if (isVerifyCancelledRef.current) return;

        if (response) {
          const isConnected = response.status === 'CONNECTED';
          const isPendingOpen = response.config?.connection_status === 'open';

          if (isConnected || isPendingOpen) {
            const finalCheck = await fetchStatus();
            if (finalCheck?.status === 'CONNECTED') {
              return;
            }
          }

          // QR dinámico: actualizar si el backend regeneró el código
          if (response.status === 'QR_CODE' && response.qrcode) {
            setState((prev) => ({
              ...prev,
              qrCode: response.qrcode!,
              connectionStatus: 'QR_CODE',
            }));
          }
        }

        attempts++;
        if (attempts < maxAttempts) {
          await delay(pollInterval);
        }
      }

      if (!isVerifyCancelledRef.current) {
        toast.error('No se detectó la conexión. Intenta escanear nuevamente.');
        setState((prev) => ({ ...prev, isVerifying: false }));
      }
    } catch (error) {
      if (!isVerifyCancelledRef.current) {
        setState((prev) => ({
          ...prev,
          isVerifying: false,
          error: 'No se pudo verificar la conexión.',
        }));
      }
    }
  }, [integration, syncIntegration, fetchStatus]);

  const disconnect = useCallback(async () => {
    // Marcar desconexión ANTES de cualquier operación async
    // para que handleConnectionResponse ignore respuestas CONNECTED en vuelo
    isDisconnectingRef.current = true;
    // Cancelar polling de verificación si está activo
    isVerifyCancelledRef.current = true;

    if (!integration?.id) {
      lastCheckedIdRef.current = null;
      setState(INITIAL_STATE);
      isDisconnectingRef.current = false;
      return;
    }

    setState((prev) => ({ ...prev, isConnecting: true }));

    try {
      await removeIntegration(integration as unknown as Integration);
      lastCheckedIdRef.current = null;
      setState(INITIAL_STATE);
    } catch (error) {
      setState((prev) => ({ ...prev, isConnecting: false }));
    } finally {
      isDisconnectingRef.current = false;
    }
  }, [integration, removeIntegration]);

  const closeQrModal = useCallback(() => {
    isVerifyCancelledRef.current = true;
    setState((prev) => ({
      ...prev,
      isQrVisible: false,
      isVerifying: false,
      qrCode: null,
      connectionStatus: prev.connectionStatus === 'QR_CODE' ? 'DISCONNECTED' : prev.connectionStatus,
    }));
  }, []);

  // Cleanup: cancel polling on unmount
  useEffect(() => {
    return () => {
      isVerifyCancelledRef.current = true;
    };
  }, []);

  return {
    isConnected: state.connectionStatus === 'CONNECTED',
    isConnecting: state.isConnecting,
    connect,
    disconnect,
    status: state.connectionStatus,
    instanceName: state.instanceName,
    isQrVisible: state.isQrVisible,
    qrCode: state.qrCode,
    onVerify: verifyConnection,
    onCloseQr: closeQrModal,
    isVerifying: state.isVerifying,
  };
};
